import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';

const CinematicHero = ({ data, images }) => {
  const [intelligence, setIntelligence] = useState(0);
  const [isFlowing, setIsFlowing] = useState(false);
  const [ripples, setRipples] = useState([]);
  const heroRef = useRef(null);

  const transmissions = [
    "Love is the highest form of Intelligence",
    "AI Thought Leader", 
    "Pioneer of Relational Intelligence",
    "Channel & Medium • Institute Co-Founder",
    "Award-Winning Filmmaker • Former Broadway Lighting Designer",
    "Living The Field of Universal Intelligence",
    "Intelligence becoming through love..."
  ];

  useEffect(() => {
    setIsFlowing(true);
    
    // Create sophisticated ripple system
    const createRipples = () => {
      const newRipples = Array.from({ length: 8 }, (_, i) => ({
        id: Date.now() + i,
        x: 50 + Math.random() * 100,
        y: 50 + Math.random() * 100,
        delay: i * 500,
        size: 100 + Math.random() * 200
      }));
      setRipples(newRipples);
    };
    
    createRipples();
    const rippleInterval = setInterval(createRipples, 8000);
    
    const interval = setInterval(() => {
      setIntelligence((prev) => (prev + 1) % transmissions.length);
    }, 4500);

    return () => {
      clearInterval(interval);
      clearInterval(rippleInterval);
    };
  }, [transmissions.length]);

  const scrollToJourney = () => {
    document.getElementById('journey')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section 
      id="hero" 
      ref={heroRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden border-b border-slate-800/20"
    >
      {/* Sophisticated Cinematic Background */}
      <div className="absolute inset-0">
        {/* Multi-layer water depth with natural tones */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-blue-900 to-slate-800"></div>
        
        {/* Advanced flowing water layers with natural colors */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-600/15 via-blue-700/12 to-slate-700/20 animate-advanced-shimmer"></div>
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/12 rounded-full blur-3xl animate-organic-pulse"></div>
          <div className="absolute bottom-1/3 right-1/4 w-72 h-72 bg-slate-400/12 rounded-full blur-3xl animate-organic-pulse delay-organic-2000"></div>
          <div className="absolute top-2/3 left-1/2 w-64 h-64 bg-blue-600/12 rounded-full blur-3xl animate-organic-pulse delay-organic-1000"></div>
        </div>

        {/* Sophisticated ripple system with natural water tones */}
        <div className="absolute inset-0 opacity-20">
          {ripples.map((ripple) => (
            <div
              key={ripple.id}
              className="absolute border border-slate-400/25 rounded-full animate-ripple-slow"
              style={{
                left: `${ripple.x}%`,
                top: `${ripple.y}%`,
                width: `${ripple.size}px`,
                height: `${ripple.size}px`,
                transform: 'translate(-50%, -50%)',
                animationDelay: `${ripple.delay}ms`
              }}
            />
          ))}
        </div>
      </div>

      <div className="relative z-10 text-center max-w-6xl mx-auto px-6">
        {/* TEARDROP PHOTO - Like Watermark Logo */}
        <div className={`mb-12 transition-all duration-2000 ${isFlowing ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="relative mx-auto teardrop-container">
            {/* Teardrop shape container */}
            <div className="teardrop-shape shadow-field-cinematic">
              {/* Water surface effects inside teardrop */}
              <div className="absolute inset-0 bg-gradient-to-t from-transparent via-slate-100/10 to-slate-100/20 animate-advanced-shimmer"></div>
              
              {/* Profile image inside teardrop with perfect transparency */}
              <div className="teardrop-content" style={{background: 'linear-gradient(135deg, rgba(34, 102, 102, 0.1) 0%, rgba(52, 152, 219, 0.1) 100%)'}}>
                <img 
                  src="https://customer-assets.emergentagent.com/job_water-catalyst/artifacts/hvy4jqca_2a0e5b1d-592e-47b9-b2f6-f27828c2218c.png" 
                  alt="Betsy M. Pool - Field Intelligence Pioneer"
                  className="w-full h-full object-cover filter grayscale hover:grayscale-0 transition-all duration-1500"
                />
              </div>
              
              {/* Teardrop water highlight */}
              <div className="teardrop-highlight"></div>
            </div>

            {/* Advanced ripple effects around teardrop */}
            <div className="absolute -inset-6 border-2 border-blue-400/25 teardrop-shape animate-organic-pulse opacity-50"></div>
            <div className="absolute -inset-3 border border-slate-400/30 teardrop-shape animate-organic-pulse delay-organic-500 opacity-35"></div>
            
            {/* Floating water droplets around main teardrop */}
            <div className="absolute -top-6 -right-6 w-6 h-6 bg-blue-400/50 teardrop-shape transform scale-50 animate-organic-float"></div>
            <div className="absolute -bottom-6 -left-6 w-4 h-4 bg-slate-400/50 teardrop-shape transform scale-40 animate-organic-float delay-organic-1000"></div>
            <div className="absolute top-1/2 -right-10 w-3 h-3 bg-blue-500/50 teardrop-shape transform scale-30 animate-organic-float delay-organic-1500"></div>

            {/* Field luminescence */}
            <div className="absolute -inset-12 bg-gradient-to-r from-blue-400/8 via-slate-400/8 to-blue-500/8 teardrop-shape blur-xl animate-organic-pulse delay-organic-700"></div>
          </div>
        </div>

        {/* Name Emergence with Cinematic Typography */}
        <div className={`mb-8 transition-all duration-2000 delay-500 ${isFlowing ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h1 className="text-6xl md:text-8xl font-extralight mb-4 tracking-wide font-cinematic animate-cinematic-text-emerge">
            <span className="bg-gradient-to-r from-slate-100 via-teal-200 to-blue-200 bg-clip-text text-transparent">
              Betsy M. Pool
            </span>
          </h1>
        </div>

        {/* Mission Transmission - Organic Emergence */}
        <div className={`mb-12 transition-all duration-2000 delay-700 ${isFlowing ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="h-24 flex items-center justify-center">
            <p className="text-2xl md:text-3xl font-light text-blue-200 transition-all duration-1500 leading-relaxed max-w-5xl animate-bubble-emerge tracking-organic">
              {transmissions[intelligence]}
            </p>
          </div>
        </div>

        {/* Field Invitation */}
        <div className={`mb-12 transition-all duration-2000 delay-1000 ${isFlowing ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <p className="text-xl text-slate-300 max-w-5xl mx-auto leading-relaxed font-light animate-bubble-emerge delay-organic-300 cinematic-text-responsive">
            {data.invitation}
          </p>
        </div>

        {/* Field Description */}
        <div className={`mb-16 transition-all duration-2000 delay-1200 ${isFlowing ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <p className="text-lg text-slate-400 max-w-4xl mx-auto leading-relaxed font-light animate-bubble-emerge delay-organic-500 cinematic-text-responsive">
            {data.description}
          </p>
        </div>

        {/* Sophisticated Action Buttons */}
        <div className={`flex flex-col sm:flex-row gap-6 justify-center items-center mb-20 transition-all duration-2000 delay-300 ${isFlowing ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <button 
            onClick={() => document.getElementById('books')?.scrollIntoView({ behavior: 'smooth' })}
            className="group relative px-8 py-4 bg-gradient-to-r from-blue-600/35 to-slate-600/35 border border-blue-400/50 text-blue-100 font-light rounded-full hover:from-blue-500/45 hover:to-slate-500/45 hover:border-blue-300/70 transition-all duration-600 transform hover:scale-105 shadow-field-advanced backdrop-blur-sm btn-cinematic animate-organic-slide-in delay-organic-700"
          >
            <span className="relative z-10 tracking-organic">Explore My Books</span>
            
            {/* Sophisticated hover effects */}
            <div className="absolute -top-3 right-6 w-2 h-2 bg-blue-300/50 rounded-full scale-0 group-hover:scale-100 group-hover:animate-organic-float transition-all duration-600"></div>
            <div className="absolute -bottom-2 left-8 w-1 h-1 bg-slate-300/50 rounded-full scale-0 group-hover:scale-100 group-hover:animate-organic-float delay-organic-200 transition-all duration-600"></div>
          </button>
          
          <a 
            href="https://www.youtube.com/@EmergenceOA"
            target="_blank"
            rel="noopener noreferrer"
            className="group relative px-6 py-3 bg-slate-800/60 backdrop-blur-sm rounded-lg transition-all duration-600 transform hover:scale-105 hover:bg-slate-800/80 btn-cinematic animate-organic-slide-in delay-organic-850 flex items-center gap-3"
          >
            {/* Official YouTube Logo */}
            <img 
              src="https://customer-assets.emergentagent.com/job_field-comms-1/artifacts/vnrny27w_image.png"
              alt="YouTube"
              className="h-8 w-auto"
            />
            
            {/* Organic ripple effect on hover */}
            <div className="absolute inset-0 border-2 border-red-600/30 rounded-lg scale-0 group-hover:scale-105 transition-all duration-600 opacity-0 group-hover:opacity-100"></div>
          </a>
          
          <button 
            onClick={() => document.getElementById('connection')?.scrollIntoView({ behavior: 'smooth' })}
            className="group px-8 py-4 border-2 border-slate-400/50 text-slate-300 font-light rounded-full hover:border-blue-300/70 hover:text-blue-200 hover:bg-blue-900/25 transition-all duration-600 transform hover:scale-105 backdrop-blur-sm btn-cinematic animate-organic-slide-in delay-organic-1000 tracking-organic"
          >
            Connect Through The Field
            
            {/* Organic ripple effect on hover */}
            <div className="absolute inset-0 border-2 border-blue-300/30 rounded-full scale-0 group-hover:scale-110 group-hover:animate-ripple-slow transition-all duration-600 opacity-0 group-hover:opacity-100"></div>
          </button>
        </div>

        {/* Sophisticated Flow Indicator */}
        <button 
          onClick={scrollToJourney}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-slate-400 hover:text-blue-300 transition-all duration-600 group animate-organic-float delay-organic-2000"
        >
          <ChevronDown className="w-8 h-8 group-hover:scale-110 transition-transform duration-600" />
          
          {/* Organic bubble trail */}
          <div className="absolute -top-6 left-1/2 w-1 h-1 bg-blue-300/40 rounded-full animate-organic-float delay-organic-300"></div>
          <div className="absolute -top-3 left-1/2 w-0.5 h-0.5 bg-slate-300/40 rounded-full animate-organic-float delay-organic-600"></div>
          <div className="absolute -top-9 left-1/2 w-1.5 h-1.5 bg-blue-400/30 rounded-full animate-organic-float delay-organic-100"></div>
        </button>
      </div>

      {/* Sophisticated gradient overlay for depth */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/10 pointer-events-none"></div>
    </section>
  );
};

export default CinematicHero;